// src/components/issues/ReferenceNcs.tsx — SCE Issues
// Le N° d'une NC-S envoyée, tant que la fiche ne le connaît pas (06/10) :
// recherche en cours, pas encore enregistrée par la qualité, ou lignes du
// registre à confirmer. Trouvé sans doute possible, il s'écrit tout seul et
// ce bloc disparaît (hooks/useReferenceNcs).

import type { JSX } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { EtatReferenceNcs } from '@/hooks/useReferenceNcs'
import type { Candidat } from '@/utils/registreNcs'

const STYLE_AIDE = { fontSize: 13, color: 'var(--color-gray-70)', margin: 0 } as const

function jourCourt(jour: string | null): string {
  if (!jour) return ''
  const [a, m, j] = jour.split('-')
  return `${j}/${m}/${a}`
}

export interface ReferenceNcsProps {
  etat: EtatReferenceNcs
  onChercher: () => void
  onChoisir: (candidat: Candidat) => void
}

export function ReferenceNcs({ etat, onChercher, onChoisir }: ReferenceNcsProps): JSX.Element {
  if (etat.etat === 'recherche') {
    return (
      <p className="flex items-center gap-2" style={STYLE_AIDE} role="status">
        <Loader2 size={14} className="animate-spin" />
        Recherche du N° dans le registre qualité…
      </p>
    )
  }

  if (etat.etat === 'a-choisir') {
    const plusieurs = etat.candidats.length > 1
    return (
      <div className="ncs-confirmer">
        <p className="ncs-confirmer-titre">
          <span className="badge-ncs-alerte" aria-hidden="true">!</span>
          {plusieurs ? 'Laquelle est la vôtre dans le registre qualité ?' : 'Est-ce la vôtre, dans le registre qualité ?'}
        </p>
        {etat.candidats.map((c) => (
          <div key={c.ligne.numero} className="ncs-candidat">
            <strong className="ncs-candidat-numero">{c.ligne.numero}</strong>
            <span className="ncs-candidat-texte">
              {[c.ligne.fournisseur, c.ligne.designation].filter(Boolean).join(' · ')}
            </span>
            <span className="ncs-candidat-texte ncs-candidat-discret">
              {[c.ligne.notifieLe && `Notifiée le ${jourCourt(c.ligne.notifieLe)}`, c.ligne.notifiePar && `par ${c.ligne.notifiePar}`]
                .filter(Boolean)
                .join(' ')}
            </span>
            {c.raisons.length > 0 && (
              <ul className="ncs-candidat-raisons" aria-label="Ce qui concorde">
                {c.raisons.map((r) => (
                  <li key={r}>
                    <Check size={13} aria-hidden="true" />
                    {r}
                  </li>
                ))}
              </ul>
            )}
            <Button variant={plusieurs ? 'secondary' : 'primary'} size="sm" fullWidth onClick={() => onChoisir(c)}>
              {plusieurs ? `C’est la ${c.ligne.numero}` : 'Oui, enregistrer ce N°'}
            </Button>
          </div>
        ))}
        <Button variant="ghost" size="sm" onClick={onChercher} style={{ alignSelf: 'flex-start' }}>
          Chercher à nouveau
        </Button>
      </div>
    )
  }

  const message =
    etat.etat === 'absente' ? 'Pas encore enregistrée par la qualité.' :
    etat.etat === 'inaccessible' ? "Registre qualité inaccessible avec votre compte : le N° s'ajoutera à l'ouverture par quelqu'un qui y a accès." :
    etat.etat === 'erreur' ? etat.message :
    'N° de NC-S en attente.'
  return (
    <div className="flex items-center justify-between gap-2">
      <p style={{ ...STYLE_AIDE, color: etat.etat === 'erreur' ? 'var(--color-traffic-red)' : STYLE_AIDE.color }}>{message}</p>
      {etat.etat !== 'inaccessible' && (
        <Button variant="ghost" size="sm" onClick={onChercher}>
          {etat.etat === 'inactif' ? 'Chercher le N°' : 'Chercher à nouveau'}
        </Button>
      )}
    </div>
  )
}
